import type { AvailabilityWindow } from "@repo/api";
import type { ScheduleDto } from "@repo/data-store";
import { normalizeToMinutes } from "@repo/domain";
import { addMinutes, format, parse, parseISO, setHours, setMinutes } from "date-fns";
import { DEFAULT_SLOT_DURATION_MINUTES } from "@/features/scheduling/lib/constants";

/** Parse slot duration from schedule extension (default 30 min) */
export function getSlotDurationMinutes(schedule: ScheduleDto | null | undefined): number {
  const params = schedule?.schedulingParameters?.[0];
  if (!params) return DEFAULT_SLOT_DURATION_MINUTES;

  return normalizeToMinutes(params.duration);
}

/**
 * Drop a calendar `TZDate` to a plain Date. date-fns keeps the input's type,
 * so a TZDate leaking into a form serializes with an offset the API rejects.
 */
export function toPlainDate(date: Date): Date {
  return new Date(date.getTime());
}

/** Combine date and time into ISO string */
export function combineDateTimeISO(date: Date, time: string): string {
  const [hours, minutes] = time.split(":").map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    throw new Error(`Invalid time format: "${time}"`);
  }
  const result = setMinutes(setHours(date, hours), minutes);
  return result.toISOString();
}

export interface TimeSlot {
  startTime: string; // ISO datetime
  endTime: string; // ISO datetime
  dateDisplay: string; // "Wed, Mar 11"
  display: string; // "9:00 AM"
  endDisplay: string; // "9:30 AM"
}

/** Split availability windows into bookable time slots.
 *  When a window is already slot-sized (duration ≤ requested), it is used as-is
 *  (server pre-computes availability accounting for service duration). */
export function splitWindowsIntoSlots(windows: AvailabilityWindow[], durationMinutes: number): TimeSlot[] {
  const slots: TimeSlot[] = [];

  for (const window of windows) {
    const start = parseISO(window.start);
    const end = parseISO(window.end);
    const windowMinutes = (end.getTime() - start.getTime()) / 60_000;

    if (windowMinutes <= durationMinutes) {
      // Window is already a bookable slot — use as-is
      slots.push({
        startTime: window.start,
        endTime: window.end,
        dateDisplay: format(start, "EEE, MMM d"),
        display: format(start, "h:mm a"),
        endDisplay: format(end, "h:mm a"),
      });
      continue;
    }

    // Subdivide larger windows into slots
    let cursor = start;
    while (true) {
      const slotEnd = addMinutes(cursor, durationMinutes);
      if (slotEnd > end) break;

      slots.push({
        startTime: cursor.toISOString(),
        endTime: slotEnd.toISOString(),
        dateDisplay: format(cursor, "EEE, MMM d"),
        display: format(cursor, "h:mm a"),
        endDisplay: format(slotEnd, "h:mm a"),
      });
      cursor = slotEnd;
    }
  }

  return slots.sort((a, b) => a.startTime.localeCompare(b.startTime));
}

/** Calculate end time from start time and duration */
export function calculateEndTime(startTime: string, durationMinutes: number): string {
  if (!startTime || startTime.includes("--")) return "";
  const start = parse(startTime, "HH:mm", new Date());
  const end = addMinutes(start, durationMinutes);
  return format(end, "HH:mm");
}
